import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../features/auth';

export const Route = createFileRoute('/403')({
  component: UnauthorizedPage,
});

function UnauthorizedPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuthContext();
  const { t } = useTranslation(['periverse', 'common']);

  // If user is not authenticated, redirect to root (root will auto-redirect to Zitadel)
  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: '/' });
    }
  }, [isAuthenticated, navigate]);

  const handleGoHome = () => {
    navigate({ to: '/', search: {} });
  };

  // Show loading state if checking authentication
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Main Error Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          {/* Error Code */}
          <div className="mb-6">
            <div className="text-6xl font-bold text-gray-400 mb-2">403</div>
            <h1 className="text-xl font-medium text-gray-600 mb-2">
              {t('errors.accessDenied', { ns: 'periverse' })}
            </h1>
          </div>

          {/* Error Message */}
          <div className="mb-8">
            <p className="text-gray-500 mb-4 leading-relaxed">
              {t('errors.accessDeniedDescription', { ns: 'periverse' })}
            </p>
            {user && (
              <div className="bg-gray-50 rounded border border-gray-200 p-4 mb-4">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">
                    {t('user.currentRole', { ns: 'periverse' })}:
                  </span>{' '}
                  {user.isAdmin
                    ? 'ADMIN'
                    : user.permissions?.length
                      ? user.permissions.join(', ')
                      : 'USER'}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">
                    {t('user.username', { ns: 'periverse' })}:
                  </span>{' '}
                  {user.username}
                </p>
              </div>
            )}
            <p className="text-xs text-gray-400">
              {t('errors.contactAdmin', { ns: 'periverse' })}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <button
              onClick={handleGoHome}
              className="w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded text-sm font-medium transition-colors"
            >
              {t('navigation.home', { ns: 'periverse' })}
            </button>
            <button
              onClick={() => logout()}
              className="w-full bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded text-sm font-medium transition-colors"
            >
              {t('navigation.logout', { ns: 'periverse' })}
            </button>
          </div>

          {/* Additional Help */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-400 mb-2">
              {t('help.needHelp', { ns: 'periverse' })}
            </p>
            <Link
              to="/help-support"
              className="inline-flex items-center text-gray-500 hover:text-gray-600 text-xs"
            >
              {t('help.contactSupport', { ns: 'periverse' })}
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4">
          <p className="text-xs text-gray-400">
            {t('footer.tagline', { ns: 'periverse' })}
          </p>
        </div>
      </div>
    </div>
  );
}
